/**
 * propertyCache.js
 *
 * Two-layer cache: in-memory (survives re-renders) + sessionStorage (survives
 * component unmount / back-navigation). TTL = 10 s — faster updates for admin
 * changes while still providing instant back-navigation experience.
 */

const TTL = 10_000; // Reduced from 60s to 10s for faster admin updates

// In-memory layer — fastest, cleared only on full page reload
const mem = {};

export function readCache(key) {
  // 1. memory hit
  if (mem[key] && Date.now() - mem[key].ts < TTL) return mem[key].data;

  // 2. sessionStorage hit
  try {
    const raw = sessionStorage.getItem(`__pc_${key}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts < TTL) {
        mem[key] = parsed; // warm memory from storage
        return parsed.data;
      }
    }
  } catch (_) {}

  return null;
}

export function writeCache(key, data) {
  const entry = { data, ts: Date.now() };
  mem[key] = entry;
  try {
    sessionStorage.setItem(`__pc_${key}`, JSON.stringify(entry));
  } catch (_) {
    // quota exceeded — memory-only is fine
  }
}

// Clear cache for a specific key
export function clearCache(key) {
  delete mem[key];
  try {
    sessionStorage.removeItem(`__pc_${key}`);
  } catch (_) {}
}

// Clear all property caches
export function clearAllPropertyCaches() {
  // Clear memory cache
  Object.keys(mem).forEach(key => delete mem[key]);
  
  // Clear sessionStorage cache
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('__pc_')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (_) {}
}
